import * as ngCore from '@angular/core'
import * as ents from '@attain-apps/entities'
import * as ds from '@attain-apps/fe/web/main/lib/design'
import * as ec from '@attain-apps/fe/web/main/lib/entity-collection'
import * as router from '@attain-apps/fe/web/main/lib/router'
import * as rxjs from 'rxjs'

import * as dms from '../../../dms'
import * as me from '../../../me'
import * as routeBound from '../../../route-bound'
import * as records from '../records'

import { Api } from './api.service'
import associations from './associations'
import * as plugins from './plugins'
import type { TypeParams } from './types'

const SPACE_DIMENSION_ID = ents.core.dms.models.Dimension.Id.from(
  ents.extra.quiver.resources.data.dimensions.space.RESOURCE.payload,
)

/** Lab assignments collection for fetching space record lab assignments */
@ngCore.Injectable()
export class Collection extends ec.Collection<TypeParams, typeof associations> {
  constructor() {
    const scenarioContext = ngCore.inject(routeBound.scenario.Context.TOKEN)
    const recordsEc = ngCore.inject(records.Collection)
    const schemaFeature = ngCore.inject(dms.schema.Feature)
    const meFeature = ngCore.inject(me.Feature)
    const _router = ngCore.inject(router.Router)

    super({
      api: ngCore.inject(Api),
      associations,
      context$: rxjs
        .combineLatest([
          scenarioContext,
          recordsEc.plugins.currentEntityPlugin.feature.selectors.getId()
            .asObservable,
        ])
        .pipe(
          rxjs.map(([context, spaceRecordId]) => {
            if (!context || !ents.core.dms.data.Record.Id.isId(spaceRecordId)) {
              return undefined
            }

            return { ...context, spaceRecordId }
          }),
        ),
      identify: labAssignment => labAssignment.id,
      name: ec.Name.Predefined.SpaceLabAssignment,
      plugins: {
        additionalFactories: [
          new plugins.functionalUsage.Factory(),
          new ds.table.ec.Factory<TypeParams>(collection => ({
            rowConfig: ({ entity: labAssignment }) => {
              // Lab assignments are locked once the room's survey is signed off
              const canEditLabAssignment =
                recordsEc.plugins.currentEntityPlugin.feature.selectors
                  .getEntity()
                  .asSignal()?.surveyStatus !==
                ents.extra.space.SurveyStatus.SignedOff

              return {
                iconsConfigs: [
                  ...(canEditLabAssignment
                    ? [
                        {
                          class: 'atn-icon atn-trash atn-red',
                          onClick: () => {
                            collection.plugins.editingPlugin.feature.actions.markForDeletion
                              .from(
                                `LabAssignments.Table.Row-${labAssignment.id}.Icons.Trash.Clicked`,
                              )
                              .dispatch({ id: labAssignment.id })
                          },
                        },
                      ]
                    : []),
                ],
                onClick: () => {
                  const source = `LabAssignments.Table.Row-${labAssignment.id}.Clicked`

                  // The row entity is a lab assignment; the lab's space record
                  // id lives in its space-dimension lookup.
                  const schema = schemaFeature.selectors.schema.asSignal()
                  const leafSpaceEntityId =
                    schema.tryGetLeafEntityOf(SPACE_DIMENSION_ID)?.id
                  const spaceRecordId = leafSpaceEntityId
                    ? labAssignment.spaceRecordLookup[leafSpaceEntityId]?.id
                    : undefined

                  if (!ents.extra.space.Record.Id.isId(spaceRecordId)) {
                    return
                  }

                  recordsEc.plugins.currentEntityPlugin.storage.actions.putInIdLookup
                    .from(source)
                    .dispatch({ value: spaceRecordId })

                  const url = meFeature.contexts.selectors
                    .getUrlForLink({
                      goto: {
                        navigationId:
                          ents.extra.prebuilt.Page.Id.SpaceRecordDetails,
                      },
                    })
                    .asSignal()

                  if (url) {
                    _router.actions.navigate.from(source).dispatch({ url })
                  }
                },
              }
            },
          })),
        ],
      },
    })
  }
}
